import React from "react";
import { Link } from "react-router-dom";
import { CV, SEESAW_WORKS, BOOKS } from "../data/site";
import { ExternalLink, Download, ArrowRight } from "lucide-react";

export default function CVPage() {
  return (
    <div data-testid="cv-page">
      <header className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="label mb-4">Curriculum Vitae</p>
            <h1 className="serif text-5xl md:text-7xl tracking-tight font-light leading-[1.02]">
              Joseph Graves Jr.
            </h1>
            <p className="mt-4 serif italic text-xl md:text-2xl text-[hsl(var(--ink-soft))]">
              {CV.born} · {CV.basedIn}
            </p>
          </div>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/press-kit`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 border border-foreground px-6 py-3.5 text-xs uppercase tracking-[0.18em] hover:bg-foreground hover:text-background transition-colors self-start md:self-auto"
            data-testid="cv-request"
          >
            <Download className="h-3.5 w-3.5" />
            Download CV / Press Kit (PDF)
          </a>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-32 space-y-16 md:space-y-24">
        <Section number="01" title="Education">
          <List>
            {CV.education.map((e, i) => (
              <Row
                key={i}
                left={e.year}
                right={e.detail}
                testid={`cv-edu-${i}`}
              />
            ))}
          </List>
        </Section>

        <Section number="02" title="Solo Exhibitions">
          <List>
            {CV.soloExhibitions.map((e, i) => (
              <ExRow key={i} e={e} testid={`cv-solo-${i}`} />
            ))}
          </List>
        </Section>

        <Section number="03" title="Selected Group Exhibitions">
          <List>
            {CV.groupExhibitions.map((e, i) => (
              <ExRow key={i} e={e} testid={`cv-group-${i}`} />
            ))}
          </List>
        </Section>

        <Section number="04" title="Permanent Installations">
          <List>
            {CV.permanentInstallations.map((e, i) => (
              <ExRow key={i} e={e} testid={`cv-install-${i}`} />
            ))}
          </List>
        </Section>

        <Section
          number="05"
          title="Selected Works · In Plain Sight (2025)"
        >
          <p className="text-sm text-[hsl(var(--ink-soft))] mb-4 max-w-2xl">
            Selected works from the solo exhibition at SeeSaw Gallery,
            Englewood, CO. Available by inquiry.
          </p>
          <ul className="border-t border-border divide-y divide-border">
            {SEESAW_WORKS.map((w, i) => (
              <li
                key={i}
                className="grid grid-cols-12 gap-4 py-5 items-baseline"
                data-testid={`cv-seesaw-${i}`}
              >
                <span className="col-span-2 md:col-span-1 label">{w.year}</span>
                <div className="col-span-10 md:col-span-11">
                  <p className="serif italic text-xl md:text-2xl">{w.title}</p>
                  <p className="text-sm text-[hsl(var(--ink-soft))] mt-1">
                    {w.medium} · {w.size}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </Section>

        <Section number="06" title="Children's Literature">
          <List>
            {BOOKS.map((b, i) => (
              <li
                key={b.id}
                className="grid grid-cols-12 gap-4 py-5 items-baseline"
                data-testid={`cv-book-${b.id}`}
              >
                <span className="col-span-2 md:col-span-1 label">{b.year}</span>
                <div className="col-span-10 md:col-span-11">
                  <p className="serif italic text-xl md:text-2xl">{b.title}</p>
                  <p className="text-sm text-[hsl(var(--ink-soft))] mt-1">
                    {b.role}
                    {b.badge ? ` · ${b.badge}` : ""}
                  </p>
                </div>
              </li>
            ))}
          </List>
          <Link
            to="/books"
            className="mt-6 inline-flex items-center gap-2 label link-underline"
          >
            See the books <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </Section>

        <Section number="07" title="Recognition & Commissions">
          <List>
            {CV.recognition.map((r, i) => (
              <Row
                key={i}
                left={r.year}
                right={
                  <>
                    <p className="serif text-xl md:text-2xl">{r.title}</p>
                    <p className="label mt-1">{r.detail}</p>
                  </>
                }
                testid={`cv-recognition-${i}`}
              />
            ))}
          </List>
        </Section>

        <Section number="08" title="Press">
          <List>
            {CV.press.map((p, i) => (
              <li
                key={i}
                className="grid grid-cols-12 gap-4 py-5 items-baseline"
                data-testid={`cv-press-${i}`}
              >
                <span className="col-span-2 md:col-span-1 label">{p.year}</span>
                <div className="col-span-9 md:col-span-9">
                  <p className="serif text-xl">{p.outlet}</p>
                  <p className="serif italic text-base text-[hsl(var(--ink-soft))]">
                    {p.title}
                  </p>
                </div>
                <a
                  href={p.link}
                  target="_blank"
                  rel="noreferrer"
                  className="col-span-1 md:col-span-2 text-right label link-underline inline-flex items-center gap-1 justify-end"
                >
                  Read <ExternalLink className="h-3 w-3" />
                </a>
              </li>
            ))}
          </List>
          <Link
            to="/press"
            className="mt-6 inline-flex items-center gap-2 label link-underline"
          >
            See full press page <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </Section>

        <Section number="09" title="Galleries & Venues Shown">
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-3 border-t border-border pt-5">
            {[...new Set(CV.galleriesShown)].map((g, i) => (
              <li
                key={i}
                className="serif text-lg text-foreground py-1"
                data-testid={`cv-gallery-${i}`}
              >
                <span className="serif italic text-[hsl(var(--accent))] mr-2">
                  —
                </span>
                {g}
              </li>
            ))}
          </ul>
        </Section>

        <Section number="10" title="Professional Experience">
          <List>
            {CV.professional.map((p, i) => (
              <Row
                key={i}
                left={p.year || ""}
                right={p.role}
                testid={`cv-prof-${i}`}
              />
            ))}
          </List>
        </Section>

        <Section number="11" title="Teaching & Practice">
          <ul className="border-t border-border divide-y divide-border">
            {CV.teachingAndPractice.map((t, i) => (
              <li
                key={i}
                className="py-4 flex items-baseline gap-4"
                data-testid={`cv-teaching-${i}`}
              >
                <span className="serif italic text-[hsl(var(--accent))] shrink-0">
                  ◦
                </span>
                <p className="serif text-lg leading-snug">{t}</p>
              </li>
            ))}
          </ul>
        </Section>

        <Section number="12" title="Representation">
          <p className="serif text-2xl md:text-3xl border-t border-border pt-5 leading-snug">
            {CV.representation}
          </p>
        </Section>

        <div className="border-t border-border pt-12 flex flex-col md:flex-row gap-6 items-start md:items-center justify-between">
          <p className="serif italic text-lg text-[hsl(var(--ink-soft))]">
            For inquiries, image requests, or a downloadable CV, contact the
            studio.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="bg-foreground text-background px-7 py-3.5 text-xs uppercase tracking-[0.18em] hover:opacity-90"
            >
              Contact the studio
            </Link>
            <Link
              to="/commissions"
              className="border border-foreground px-7 py-3.5 text-xs uppercase tracking-[0.18em] hover:bg-foreground hover:text-background transition-colors"
            >
              Commission a piece
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ number, title, children }) {
  return (
    <section
      className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16"
      data-testid={`cv-section-${number}`}
    >
      <div className="md:col-span-3">
        <p className="label">{number}</p>
        <h2 className="serif text-3xl md:text-4xl tracking-tight font-light mt-3 leading-tight">
          {title}
        </h2>
      </div>
      <div className="md:col-span-9">{children}</div>
    </section>
  );
}

function List({ children }) {
  return (
    <ul className="border-t border-border divide-y divide-border">{children}</ul>
  );
}

function Row({ left, right, testid }) {
  return (
    <li
      className="grid grid-cols-12 gap-4 py-5 items-baseline"
      data-testid={testid}
    >
      <span className="col-span-2 md:col-span-1 label">{left}</span>
      <div className="col-span-10 md:col-span-11 serif text-xl md:text-2xl text-foreground leading-snug">
        {right}
      </div>
    </li>
  );
}

function ExRow({ e, testid }) {
  return (
    <li
      className="grid grid-cols-12 gap-4 py-5 items-baseline"
      data-testid={testid}
    >
      <span className="col-span-2 md:col-span-1 label">{e.year}</span>
      <div className="col-span-10 md:col-span-11">
        <p className="serif italic text-xl md:text-2xl text-foreground leading-tight">
          {e.title}
        </p>
        <p className="mt-1 text-sm text-[hsl(var(--ink-soft))]">{e.venue}</p>
        {e.note && (
          <p className="label mt-1">
            {e.note}
            {e.link && (
              <>
                {" · "}
                <a href={e.link} target="_blank" rel="noreferrer" className="link-underline text-foreground">
                  Museum page →
                </a>
              </>
            )}
          </p>
        )}
      </div>
    </li>
  );
}
